import type { BlackAndWhiteValue } from '../lib/adjustments/black-white';
import type { BrightnessContrastValue } from '../lib/adjustments/brightness-contrast';
import type { ColorBalanceValue } from '../lib/adjustments/color-balance';
import type { CurvesValue } from '../lib/adjustments/curves';
import type { HueSaturationValue } from '../lib/adjustments/hue-saturation';
import type { LevelsValue } from '../lib/adjustments/levels';
import type { ShadowsHighlightsValue } from '../lib/adjustments/shadows-highlights';
import type { VibranceValue } from '../lib/adjustments/vibrance';

export const ARCHE_ID = 'arche_image_editor';

export type EditorTool =
  | 'move'
  | 'marquee'
  | 'crop'
  | 'draw'
  | 'text'
  | 'shape'
  | 'eraser'
  | 'fill'
  | 'retouch'
  | 'ai'
  /** Slice tool — Tier 2 Group F sub-piece #5. Defines named
   *  rectangular regions over the canvas; the ExportDialog's
   *  Region → Slices mode exports each region as a separate file
   *  in a ZIP (local) or as N sequential uploads (Fias). Slices
   *  are document-scoped metadata (not LayerNodes) persisted on
   *  the top-level `slices` state field. */
  | 'slice'
  /** Pen tool — Tier 2 Group A. Constructs vector paths via the
   *  PenPanel variant chip row (Pen / Freeform / Curvature). The
   *  default variant is Curvature, which fits a smooth Catmull-Rom
   *  curve through every clicked anchor automatically. Pen and
   *  Freeform variants offer the classic Photoshop interactions
   *  (corner + drag-for-handle / sampled drag). */
  | 'pen'
  /** Quick Mask — Tier 1 Group D mode. Brush/eraser strokes paint
   *  onto a working alpha mask rendered as a red 50% overlay. On
   *  exit (toggle off via the StatusBar selection chip popover)
   *  the mask is installed as a wand-style selection. Has no tool-
   *  palette slot — entered from the popover, exited the same way.
   *  Lives in this union so brush handlers can gate on the active
   *  tool in `useFabricCanvas`. */
  | 'quickmask'
  /** Space-drag pan — Tier 2 Group D pan/zoom polish. Held-Space
   *  override that captures drag input across every tool so the
   *  user can pan the viewport without leaving their current tool.
   *  Enters via `useFabricCanvas.setSpaceHeld(true)` (called from
   *  EditorPage's space-key tracker), exits via `setSpaceHeld(false)`
   *  which restores the prior tool / isDrawingMode / cursor. Like
   *  `quickmask`, has no tool-palette slot — entered programmatically
   *  while a key is physically held. */
  | 'space-pan';

/** Pen-tool variants — surfaced as the PenPanel chip row. */
export type PenVariant = 'pen' | 'freeform' | 'curvature';

/** Anchor type classifies how an anchor's incoming and outgoing
 *  tangent handles relate to each other:
 *    - `'corner'`     — handles independent (or absent — straight
 *                       segments on both sides)
 *    - `'smooth'`     — handles collinear AND equal-length
 *                       (continuous tangent + curvature both sides)
 *    - `'asymmetric'` — handles collinear but different lengths
 *                       (continuous tangent, broken curvature) */
export type AnchorType = 'corner' | 'smooth' | 'asymmetric';

/** A single anchor on a vector path. Position is in document scene
 *  coords (NOT normalized). Tangent handles `inX`/`inY` and
 *  `outX`/`outY` are RELATIVE to the anchor position (NOT absolute);
 *  an undefined handle means "no curvature on that side." */
export interface VectorAnchor {
  x: number;
  y: number;
  inX?: number;
  inY?: number;
  outX?: number;
  outY?: number;
  type: AnchorType;
}

/** Sub-paths are runs of anchors with a single open/closed flag.
 *  A VectorPath holds one or more sub-paths — a typical pen draw is
 *  one sub-path; boolean ops (Tier 2 Group A) produce multi-sub-
 *  path results when the result has holes or disconnected pieces. */
export interface VectorSubPath {
  closed: boolean;
  anchors: VectorAnchor[];
}

/** Stroke style for a vector path. Cap / join match canvas2d's
 *  `lineCap` / `lineJoin` so the renderer can pass them through. */
export interface VectorStroke {
  color: string;
  width: number;
  cap?: 'butt' | 'round' | 'square';
  join?: 'miter' | 'round' | 'bevel';
}

/** Top-level vector path on a `LayerNode { type: 'vector' }`. Fill
 *  reuses the existing `string | ShapeFillValue` union so vector
 *  layers and shape layers share a fill picker AND so legacy
 *  solid-hex strings (or `'transparent'`) flow through without a
 *  wrapping `{ type: 'solid', color }` literal. */
export interface VectorPath {
  subPaths: VectorSubPath[];
  stroke?: VectorStroke;
  fill?: string | ShapeFillValue;
}

/** Retouch tool variants. Each variant is a different
 *  pixel-modifying brush behavior; the user picks one via the
 *  RetouchPanel's variant chip row. The variant controls which
 *  algorithm runs against the active layer's pixels under the
 *  brush stroke. Per-variant configuration lives on
 *  `RetouchSettings.variantSettings`, keyed by variant. Tier 1
 *  step 1 (Foundation) wires up the slot + state + UI; per-
 *  variant pixel logic ships in subsequent commits. */
export type RetouchVariant =
  | 'clone'
  | 'heal'
  | 'smudge'
  | 'blur'
  | 'sharpen'
  | 'dodge'
  | 'burn'
  | 'sponge';

export type ShapeType =
  | 'rect'
  | 'circle'
  | 'line'
  | 'triangle'
  /** Tier 2 Group A primitives — each one is a vector layer, NOT
   *  a Fabric raster shape, so they're editable in Path Edit mode.
   *  Sides / points / corner radius live on `ShapeSettings`. */
  | 'polygon'
  | 'star'
  | 'arrow';
export type LayerType =
  | 'image'
  | 'text'
  | 'shape'
  | 'paint'
  | 'empty'
  | 'group'
  | 'adjustment'
  /** Vector layer — Tier 2 Group A. Backed by a `VectorPath` on
   *  `LayerNode.path` (NOT a Fabric raster object). Renders via a
   *  Fabric `Path` whose path-data string is rebuilt from the
   *  document state on every relevant change. Editable in Path
   *  Edit mode (mirrors Free Transform's modal pattern). */
  | 'vector';

/** Output mode shared by every multi-step AI tool that produces an
 *  image (Image to Image, Replace Background, Upscale, Control). Two
 *  semantics:
 *
 *    - `'replace'`   — bake the result into the active layer's pixels
 *                      in place. Destructive but keeps the layer count
 *                      constant — best for iterative refinement on the
 *                      same target.
 *    - `'new-layer'` — drop the result on top as a new editable layer
 *                      aligned to the source. Non-destructive — easy
 *                      to compare with the original by toggling
 *                      visibility, or to discard by deleting it. */
export type EditorOutputMode = 'replace' | 'new-layer';

/** AI capabilities surfaced in the AIToolPickerDialog. Each value
 *  drives one sub-panel under the AI section of PropertiesPanel. */
export type AICapability =
  | 'smart-create'
  | 'smart-delete'
  | 'inpaint'
  | 'search-replace'
  | 'search-recolor'
  | 'outpaint'
  | 'image-to-image'
  | 'remove-background'
  | 'replace-background'
  | 'upscale'
  | 'vectorize'
  | 'control';

/** Inpaint engine choice — picked from the Inpaint sub-panel's
 *  pulldown. Each engine maps server-side to a (provider, model)
 *  tuple via ENGINE_ROUTES in `services/api-service/src/routes/
 *  arches/image-editor/smart-inpaint.ts`. */
export type InpaintEngine = 'stability' | 'recraft' | 'openai';

/** Image-to-Image engine choice. All three are Stability tiers with
 *  different price/quality tradeoffs. */
export type ImageToImageEngine = 'core' | 'ultra' | 'sd3-large';

/** Remove-Background engine choice. `imgly` runs on-device (free);
 *  `stability` and `recraft` route through paid backend endpoints. */
export type RemoveBackgroundEngine = 'imgly' | 'stability' | 'recraft';

/** Replace-Background engine choice. `recraft` is the cheap default
 *  (prompt-only swap, no relighting); `stability` is the photoreal
 *  upsell with subject relighting and reference-image background. */
export type ReplaceBackgroundEngine = 'recraft' | 'stability';

/** Stability-only light-source direction for replace-background-and-relight.
 *  `'none'` is the editor sentinel — the API drops the parameter when
 *  the user hasn't picked a direction. */
export type ReplaceBackgroundLightDirection = 'none' | 'above' | 'below' | 'left' | 'right';

/** Recraft-only style preset for replace-background. Same enum as
 *  Recraft's other style-aware endpoints. */
export type ReplaceBackgroundRecraftStyle =
  | 'realistic_image'
  | 'digital_illustration'
  | 'vector_illustration'
  | 'icon';

/** Upscale modes — three distinct user intents:
 *
 *    - `'fast'`         — clean 4× upscale, no reimagining (~2 cr,
 *                         default)
 *    - `'high-quality'` — premium hi-res preserve-detail upscaler,
 *                         Stability-only (~30 cr, prompt required)
 *    - `'reimagine'`    — heavy upscale that invents new detail
 *                         (~24-30 cr) */
export type UpscaleMode = 'fast' | 'high-quality' | 'reimagine';

/** Upscale engine for modes that have a real choice (`fast` and
 *  `reimagine`). High-quality is forced to Stability — the panel
 *  hides the engine pulldown for that mode. */
export type UpscaleEngine = 'stability' | 'recraft';

/** Recraft upscale factor. Crisp + creative endpoints accept it;
 *  Stability fast is fixed at 4×. Default 4. */
export type UpscaleFactor = 2 | 4;

/** Control / Guided modes — four Stability v2beta `/control/*`
 *  endpoints surfaced as a single mode-first tool. Each mode has
 *  different required inputs:
 *
 *    - `'from-sketch'`     — sketch + prompt → polished image
 *    - `'match-structure'` — image + prompt → new content, same shape
 *    - `'match-style'`     — style ref + prompt → content in that style
 *    - `'style-transfer'`  — content + style image → blended (no prompt) */
export type ControlMode = 'from-sketch' | 'match-structure' | 'match-style' | 'style-transfer';

/** Per-layer effects applied to every Fabric object tagged with the
 *  layer id. Drop shadow + stroke round-trip through Fabric's native
 *  `.shadow` / `.stroke` (default toJSON / loadFromJSON). Glow runs
 *  through a dedicated `PixelGlowFilter` for image layers (composes
 *  alongside drop shadow + stroke filter); for non-image layers it
 *  falls back to the single `.shadow` slot, which means drop shadow
 *  wins if both are enabled on a text/shape layer. */
export interface LayerEffects {
  dropShadow?: {
    color: string;
    blur: number;
    offsetX: number;
    offsetY: number;
  };
  stroke?: {
    color: string;
    width: number;
  };
  glow?: {
    color: string;
    blur: number;
    /** Halo opacity multiplier (typical range 0.5–3). Values >1
     *  make the glow stronger by amplifying alpha; <1 softens it. */
    intensity: number;
  };
}

/** Layer composite mode. Maps directly to Canvas2D's
 *  `globalCompositeOperation` so we can pass the value to Fabric
 *  unchanged. The set is the standard Photoshop-style 16 modes
 *  grouped by intent — Photoshop's 27 has heavy overlap and the
 *  rendering of the omitted ones varies by browser. */
export type BlendMode =
  | 'source-over' // Normal
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'color-dodge'
  | 'color-burn'
  | 'hard-light'
  | 'soft-light'
  | 'difference'
  | 'exclusion'
  | 'hue'
  | 'saturation'
  | 'color'
  | 'luminosity';

/** Tier 1 extends `LayerType` with two organizational types that
 *  don't correspond to Fabric objects: `group` (children-bearing
 *  parent in the layer tree) and `adjustment` (non-destructive
 *  effect applied to layers below it in the tree). Both types are
 *  reserved here for the data model so persistence and traversal
 *  work end-to-end; the UI surfaces that produce them ship in
 *  later Tier 1 chunks. */
export interface LayerLocks {
  /** Disallow drag / Free Transform position / size changes. */
  position: boolean;
  /** Disallow paint / erase / pixel mutation. */
  pixels: boolean;
  /** Lock fully-transparent regions from being painted into. */
  transparency: boolean;
}

/** Mask attached to a layer. The mask's pixel surface is a Fabric
 *  object on the canvas (referenced by `fabricObjectId`); the
 *  fields here describe how the mask is interpreted at render time.
 *
 *  Two kinds share this type:
 *    - `'raster'` — user paints alpha directly via brush in mask
 *      mode. The RAW canvas is the source of truth; `vectorPath`
 *      is unused.
 *    - `'vector'` — `vectorPath` is the source of truth. RAW is
 *      derived (rasterized from the path); brush in mask mode is
 *      a no-op (a one-time toast in `bakeStrokeOntoMask` nudges
 *      the user to convert to a raster mask before painting).
 *
 *  Both flow through the same `materializeMaskClipPath` pipeline
 *  (PR #238) so feather / invert / density / enabled behave
 *  identically across kinds. Tier 2 Group A PR-D. */
export interface LayerMask {
  id: string;
  enabled: boolean;
  inverted: boolean;
  /** When true, the mask transforms with the layer (translate /
   *  scale / rotate together). When false, mask is anchored to the
   *  canvas regardless of layer transform. */
  linked: boolean;
  /** 0..1 — multiplied into the mask alpha at render time. 1 = full
   *  effect; 0 = mask invisible (layer fully revealed). */
  density: number;
  /** Pixel radius of a Gaussian blur applied to the mask alpha
   *  before compositing — gives soft edges without modifying
   *  the underlying mask pixels. */
  feather: number;
  fabricObjectId: string;
  /** Discriminator. Absent on legacy persisted masks → treated as
   *  `'raster'` by every reader. Tier 2 Group A PR-D. */
  kind?: 'raster' | 'vector';
  /** Vector geometry that produces the mask alpha when
   *  `kind === 'vector'`. Reducer + canvas pipeline both treat
   *  this as source of truth: changes here re-rasterize into RAW
   *  (via `setMaskVectorPath`) which re-materializes EFFECTIVE.
   *  Undefined on raster masks. */
  vectorPath?: VectorPath;
}

/** Discriminated union of adjustment-layer configs.
 *
 *  Each implemented kind's value shape is imported from
 *  `lib/adjustments/<kind>.ts` — single source of truth for the
 *  in-memory shape used by the dialogs AND the persisted shape
 *  on `LayerNode.adjustment`. The slow-path compositor reads this
 *  directly and dispatches through `ADJUSTMENT_SPECS[kind]`.
 *
 *  Future-kind placeholders (selective-color through posterize)
 *  keep inline shapes — their `lib/adjustments/` modules don't
 *  exist yet. When one of them ships, replace its inline shape
 *  with an import the same way.
 *
 *  History note: the Phase 2 unification (Tier 1 Group C) reshaped
 *  `curves` from `{ channelPoints: Record<string, [n,n][]> }` to
 *  `{ rgb, r, g, b: CurvePoint[] }` and `levels` from a
 *  `Record<string, …>` to a `{ rgb, r, g, b: LevelsChannelConfig }`
 *  shape. No persisted user data carried the old shape — adjustment
 *  LAYERS weren't surfaced in the UI before Phase 2 (Phase 1 ran
 *  the same dialogs as one-shot destructive ops, never persisting
 *  a `type: 'adjustment'` node). No migration path is needed. */
export type AdjustmentConfig =
  | ({ kind: 'curves' } & CurvesValue)
  | ({ kind: 'levels' } & LevelsValue)
  | ({ kind: 'brightness-contrast' } & BrightnessContrastValue)
  | ({ kind: 'hue-saturation' } & HueSaturationValue)
  | ({ kind: 'color-balance' } & ColorBalanceValue)
  | ({ kind: 'vibrance' } & VibranceValue)
  | ({ kind: 'shadows-highlights' } & ShadowsHighlightsValue)
  | {
      kind: 'selective-color';
      target: 'reds' | 'yellows' | 'greens' | 'cyans' | 'blues' | 'magentas';
      cyan: number;
      magenta: number;
      yellow: number;
      black: number;
    }
  | { kind: 'channel-mixer'; matrix: number[] }
  | ({ kind: 'black-white' } & BlackAndWhiteValue)
  | { kind: 'invert' }
  | { kind: 'threshold'; threshold: number }
  | { kind: 'posterize'; levels: number }
  // ── Tier 2 Group C — Live filters ───────────────────────────────
  // Filters share AdjustmentConfig because the runtime treatment is
  // identical: they're an adjustment-layer kind under the hood —
  // the only thing distinguishing them from tonal/color adjustments
  // is which picker menu surfaces them in the UI. See
  // `FILTER_MENU` in `components/adjustments/registry.ts`.
  | { kind: 'filter-gaussian-blur'; radius: number }
  | {
      kind: 'filter-motion-blur';
      /** Degrees, 0..360. 0 = horizontal, 90 = vertical, growing
       *  clockwise from the positive x-axis (screen convention). */
      angle: number;
      /** Pixels of total blur length (kernel sums both directions
       *  from each pixel). */
      distance: number;
    }
  | {
      kind: 'filter-radial-blur';
      /** Spin = rotation around `(centerX, centerY)`; Zoom =
       *  radial-stretch from the same center. */
      mode: 'spin' | 'zoom';
      /** 1..100 — converted to internal sample count / angle step. */
      amount: number;
      /** Normalized 0..1 (0,0 = top-left, 1,1 = bottom-right). Stored
       *  as fractions so the same config still makes visual sense
       *  if the layer is later resized. */
      centerX: number;
      centerY: number;
    }
  | { kind: 'filter-box-blur'; radius: number }
  // ── Sharpen family (PR-B) ──────────────────────────────────────
  /** Unsharp Mask — classic detail-enhancement filter. `amount` is a
   *  percentage (0..500) controlling how much of the (source - blur)
   *  difference is added back; `radius` (px) sets the gaussian blur
   *  used to compute the difference; `threshold` (0..255) gates
   *  sharpening per-pixel so flat regions (noise) aren't amplified. */
  | { kind: 'filter-unsharp-mask'; amount: number; radius: number; threshold: number }
  /** Smart Sharpen v1 — luminance-only Unsharp Mask with halo
   *  suppression. Operates in YCbCr so saturated edges don't pick up
   *  color fringing. `haloSuppression` (0..100) clamps the per-pixel
   *  difference contribution to prevent over-bright halos around
   *  high-contrast edges. */
  | {
      kind: 'filter-smart-sharpen';
      amount: number;
      radius: number;
      threshold: number;
      haloSuppression: number;
    }
  // ── Noise family (PR-B) ────────────────────────────────────────
  /** Add Noise — deterministic per-pixel offset derived from
   *  `hash(x, y, seed)`. `amount` (0..100) maps to a ±N pixel offset
   *  range. `distribution` picks uniform or gaussian (Box-Muller).
   *  `monochromatic: true` applies one offset to R/G/B together
   *  (luminance noise); `false` uses three independent offsets
   *  (color noise). `seed` lives on the config so the user can
   *  re-roll while keeping the non-cumulative contract. */
  | {
      kind: 'filter-add-noise';
      amount: number;
      distribution: 'uniform' | 'gaussian';
      monochromatic: boolean;
      seed: number;
    }
  /** Reduce Noise — bilateral filter. `radius` ∈ 1..5 is the spatial
   *  window half-size; `rangeSigma` controls how much color
   *  similarity is required for neighbors to influence the output;
   *  `strength` (0..100) blends the bilateral result with the
   *  source so the user can dial in subtle denoising. */
  | { kind: 'filter-reduce-noise'; radius: number; rangeSigma: number; strength: number }
  /** Median — replace each pixel with the median of its r×r
   *  neighborhood (per channel). Excellent salt-and-pepper noise
   *  removal at small radii; rapidly destroys detail at large ones.
   *  `radius` capped at 3 (7×7 window) — larger windows would block
   *  the main thread too long for an interactive preview. */
  | { kind: 'filter-median'; radius: number }
  // ── Stylize family (PR-C/D wrap-up) ─────────────────────────────
  /** Emboss — 3×3 directional gradient + grey shift. `direction`
   *  picks the apparent light source quadrant; `amount` scales the
   *  signed difference between the source and its neighbor in that
   *  direction. */
  | { kind: 'filter-emboss'; amount: number; direction: 'NE' | 'NW' | 'SE' | 'SW' }
  /** Find Edges — Sobel gradient magnitude on luminance, rendered
   *  as black-on-white grayscale (matches Photoshop). `amount`
   *  amplifies the magnitude before the invert. */
  | { kind: 'filter-find-edges'; amount: number }
  /** Posterize — per-channel quantization to N evenly-spaced output
   *  values. 2 = stark; 32 = effectively identity (used as the
   *  identity value). */
  | { kind: 'filter-posterize'; levels: number }
  /** Threshold — BT.601 luminance binarization. Output is pure
   *  white above the cutoff, pure black below. */
  | { kind: 'filter-threshold'; threshold: number }
  /** Pixelate — block-mean downsample / nearest-upsample. `blockSize`
   *  is the edge length in pixels; 1 = identity. */
  | { kind: 'filter-pixelate'; blockSize: number }
  // ── Distort family (PR-C/D wrap-up) ─────────────────────────────
  /** Lens Correction v1 — radial barrel/pincushion via
   *  `r' = r · (1 + k · r²)`. Positive `amount` → barrel (corner
   *  pixels stretch outward); negative → pincushion. */
  | { kind: 'filter-lens-correction'; amount: number }
  /** Vignette — radial brightness falloff via a smoothstep curve
   *  starting at `midpoint` (normalized 0..1) and reaching full
   *  effect at the corners. Positive `amount` darkens; negative
   *  brightens. */
  | { kind: 'filter-vignette'; amount: number; midpoint: number }
  /** Chromatic Aberration — radial per-channel offset. R samples
   *  outward, B inward, G unchanged. Offset grows linearly with
   *  distance from center. */
  | { kind: 'filter-chromatic-aberration'; amount: number }
  /** Twirl — angular rotation that decays linearly with radial
   *  distance from center. `angle` in degrees. */
  | { kind: 'filter-twirl'; angle: number }
  /** Pinch — radial deformation. Positive `amount` pulls pixels
   *  inward (pinch); negative pushes them outward (bulge). Operates
   *  only inside the unit-radius circle; outside passes through. */
  | { kind: 'filter-pinch'; amount: number };

/** Single node in the document layer tree. The tree itself lives
 *  on `Document` as a `Record<id, LayerNode>` plus an ordered
 *  `rootIds` array; nodes reference parents by `parentId` and
 *  groups list their ordered children in `childIds`.
 *
 *  This shape replaces the Tier 0 flat `Layer` interface — the old
 *  `Layer` type is now an alias of `LayerNode` (with the new
 *  fields all optional) so existing code keeps compiling while
 *  Tier 1 incrementally migrates call sites. */
export interface LayerNode {
  id: string;
  name: string;
  type: LayerType;
  visible: boolean;
  /** 0..1; defaults to 1 when omitted. Applied to every Fabric
   *  object tagged with this layer's id. */
  opacity?: number;
  /** Defaults to 'source-over' (Normal) when omitted. */
  blendMode?: BlendMode;
  /** Optional per-layer effects (drop shadow, stroke, glow).
   *  Undefined or empty = no effects. */
  effects?: LayerEffects;

  /** id of the parent group, or `null` when the layer sits at
   *  the document root. */
  parentId: string | null;

  /** Per-layer lock flags. Granular toggles surface in Tier 1's
   *  layer overflow menu via long-press; the default-unlocked
   *  state is indicated by the absence of any lock icon. */
  locks?: LayerLocks;

  /** Layer below this one in the tree gets clipped by this
   *  layer's silhouette when true. Toggled via Ctrl+Alt+G. */
  clipToBelow?: boolean;

  /** Tier 2 Group F — set when the layer's raster pixels have been
   *  modified since import (paint, eraser, retouch, paint-bucket,
   *  destructive filter Apply, non-affine commit). Used by the
   *  PSD export path: layers with a stash origin AND
   *  `rasterModified !== true` restore from the original PSD layer
   *  bytes on export; modified layers write as raster. Lives on the
   *  LayerNode so it's snapshotted by `useCanvasHistory` — undoing
   *  past a paint stroke also reverts the flag. Absent / false
   *  means "raster is intact relative to import"; explicitly true
   *  means "raster has diverged from import."
   *
   *  Mask edits, transform changes, opacity / blend-mode / name /
   *  visibility / effects edits do NOT trigger this — those are
   *  extrinsic to the raster and propagate via the export merge
   *  algorithm. */
  rasterModified?: boolean;

  /** Optional raster mask. Always 1:1 with the layer node — when
   *  the layer is deleted the mask's Fabric object is also removed. */
  mask?: LayerMask | null;

  // Discriminated by `type`:

  /** `image | text | shape | paint | empty`: id of the Fabric
   *  object on the canvas this layer renders. */
  fabricObjectId?: string;

  /** `group`: ordered list of child ids (top-most first, mirrors
   *  the Tier 0 array order). */
  childIds?: string[];

  /** `adjustment`: per-kind config that the render pipeline reads
   *  to apply the adjustment to layers below. */
  adjustment?: AdjustmentConfig;

  /** `vector`: path data. The Fabric `Path` rendering this layer
   *  is tagged with the same `layerId` as any other layer object;
   *  use `fabricObjectId` to look it up. Path edits update this
   *  field; the Fabric object is rebuilt from it. */
  path?: VectorPath;

  /** `vector` only: marks a layer whose path is generated by a
   *  shape primitive (polygon / star / arrow) so the ShapePanel
   *  sliders can regenerate the path on parameter changes.
   *  Absent on pen-drawn paths — those are user-authored geometry
   *  that sliders must NOT clobber. */
  vectorSource?: VectorSource;

  /** `text` only: when set, the text layer is bound to a vector
   *  layer's path — Fabric's IText renders glyphs along that path
   *  instead of as a straight or arched line. Mutually exclusive
   *  with `TextSettings.arch` at render time (the bound path wins;
   *  the Arch slider grays out in the panel). Auto-cleared by the
   *  reducer on `REMOVE_LAYER` of the referenced vector so a deleted
   *  path can't leave text orphaned to a missing layerId. */
  textPath?: TextPathBinding;
}

/** Binds a text layer to a vector layer's path. v1 carries only
 *  the target `pathLayerId`; `offset` / `side` knobs (Photoshop's
 *  "Start offset", "Type on top of path" toggles) are deferred to a
 *  follow-up — the Fabric IText path renderer doesn't surface them
 *  natively, so they'd need a per-frame transform on the glyph
 *  positions, which is its own chunk of work. */
export interface TextPathBinding {
  pathLayerId: string;
}

/** Marks a vector layer as generated by a shape primitive.
 *  Stored as a tag rather than a full params snapshot — params
 *  come from `state.shapeSettings`, the layer just records which
 *  generator produced it. */
export interface VectorSource {
  kind: 'polygon' | 'star' | 'arrow';
}

/** Backward-compat alias. Tier 0 code refers to this as `Layer`;
 *  during Tier 1's migration we keep the alias rather than mass-
 *  renaming every call site. */
export type Layer = LayerNode;

/** The document layer tree. Flat map of nodes keyed by id; tree
 *  shape follows from `parentId` / `childIds`. `rootIds` orders
 *  the root-level nodes top-to-bottom (front-most first).
 *
 *  Named `EditorDocument` rather than `Document` to avoid
 *  shadowing the DOM `Document` global. */
export interface EditorDocument {
  rootIds: string[];
  layers: Record<string, LayerNode>;
}

/** A named rectangular region on the canvas used by the Slice tool
 *  (Tier 2 Group F sub-piece #5). Slices are document-scoped
 *  metadata — they are NOT LayerNodes; they live on the top-level
 *  `slices` state field and persist alongside the layer document
 *  in the IDB session record. Coordinates are scene-space (same
 *  frame as `LayerNode` positions and Fabric `left` / `top`); the
 *  Fabric overlay objects rendered while the Slice tool is active
 *  carry `_isSliceOverlay: true` + `_sliceId` for filter-out.
 *
 *  At export time (Region → Slices), each slice produces one
 *  output file. The Scale multiplier (1x/2x/3x) applies per-slice;
 *  the Dimensions section's W/H is ignored (each slice's natural
 *  size × scale is authoritative). Slices that extend past the
 *  canvas bounds are clamped to the intersection at export
 *  rasterization time. */
export interface SliceRect {
  /** Stable id minted at slice creation. Used both as the React
   *  list key and as the Fabric overlay's `_sliceId` tag so the
   *  canvas-side modify event can route back to the right
   *  reducer entry. */
  id: string;
  /** User-facing name. Default "Slice N" where N is the next
   *  unused 1-indexed counter; freely renameable in SlicePanel.
   *  Used as the per-slice file name stem at export time after
   *  `slugifySliceName` sanitization. Duplicate names are allowed;
   *  the export pipeline disambiguates with `_n` suffixes. */
  name: string;
  /** Scene-space top-left in canvas pixels. Negative values are
   *  permitted (user dragged a slice partially off-canvas); the
   *  export pipeline clamps at rasterization time. */
  x: number;
  y: number;
  /** Scene-space width / height in canvas pixels. Both are kept
   *  strictly positive at the reducer boundary (negative drag is
   *  normalized on commit). Zero-area slices are rejected. */
  width: number;
  height: number;
}

export type MarqueeShape = 'rect' | 'ellipse' | 'lasso' | 'polygon' | 'wand';
/** Image export formats the editor can produce. `png` / `jpeg` /
 *  `webp` / `avif` are canvas-native (round-trip through
 *  `HTMLCanvasElement.toBlob`). `psd` is Tier 2 Group F sub-piece #2
 *  PR 2b — routed through the separate `exportDocumentToPsd` path
 *  because canvas2d can't encode PSD layer structure. `svg` is Tier 2
 *  Group F sub-piece #4 — routed through `exportDocumentToSvg` for the
 *  same reason (canvas2d can't produce a vector serialization).
 *  Callers that hand `format` to a canvas-encoder must guard against
 *  the non-canvas-native variants.
 *
 *  AVIF support on the host browser is feature-detected at the UI
 *  layer (ExportDialog's `SUPPORTS_AVIF`) — Chrome/Edge stable,
 *  Firefox 113+, Safari 16.4+. The type system accepts `'avif'`
 *  unconditionally; the dropdown disables the option dynamically when
 *  the running browser can't encode AVIF. */
export type ExportFormat = 'png' | 'jpeg' | 'webp' | 'avif' | 'tiff' | 'psd' | 'svg' | 'pdf';

/** Subset of `ExportFormat` representing single-raster formats —
 *  one frame per file, no layered or document structure. Most route
 *  through `canvas.toBlob` directly; TIFF goes through a library
 *  encoder (utif) because browsers don't natively encode TIFF, but
 *  is conceptually identical to the other rasters (no layers, no
 *  vector content). Structured formats (PSD / SVG / PDF) sit
 *  outside this set — they carry document state that can't
 *  roundtrip through `canvas.toBlob`. */
export type CanvasNativeExportFormat = Exclude<ExportFormat, 'psd' | 'svg' | 'pdf'>;

/** Formats the Fias `processImage` server endpoint accepts for
 *  Sharp-based server-side processing. Sharp supports more on the
 *  decode side, but our endpoint pins encode to the four common
 *  web raster formats. TIFF is intentionally excluded — TIFF
 *  uploads aren't supported via this endpoint in v1 (the dialog
 *  forces TIFF to local-only via `isLocalOnlyExportFormat`). */
export type FiasUploadFormat = Exclude<CanvasNativeExportFormat, 'tiff'>;

export interface BrushSettings {
  color: string;
  width: number;
  opacity: number;
  /** Soft-edge feather in PIXELS applied at bake time. 0 = the
   *  stroke bakes at its natural anti-aliased edge (matches the live
   *  preview). Larger values blur the stroke during bake — implemented
   *  as `ctx.filter = 'blur(Npx)'` on the bake `drawImage`, so the
   *  cost is GPU on most browsers and scales gracefully. Live preview
   *  still renders crisp via Fabric's PencilBrush (mirroring the
   *  pre-feather behavior); the soft edge only appears post-mouseup
   *  when the stroke bakes into the layer surface. */
  feather: number;
}

/** Settings for the Retouch tool family. Single state object that
 *  carries:
 *    - the active `variant` chip
 *    - a shared brush base (size / hardness / opacity / flow /
 *      smoothing / pressure toggles) — applies to every variant
 *    - per-variant configuration nested under each variant's key —
 *      keeps each variant's settings stable when the user cycles
 *      between them, matching Photoshop behavior. */
export interface RetouchSettings {
  variant: RetouchVariant;
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
  /** Stroke smoothing / stabilizer, 0–100. Higher = lazier
   *  follow, smoother lines. Cross-cutting — same control will
   *  apply to Brush + Eraser when the cross-cutting chunk lands. */
  smoothing: number;
  pressureSize: boolean;
  pressureOpacity: boolean;
  pressureFlow: boolean;
  clone: {
    /** Where the source pixels come from when stamping. */
    sample: 'current' | 'currentBelow' | 'all';
    /** When true, the source point translates with the stroke
     *  (Photoshop's "Aligned"). When false, every release-and-
     *  re-stroke restarts from the same source point. */
    aligned: boolean;
  };
  heal: {
    /** Blend mode for the healing operation. `normal` runs the
     *  full luminosity-match tone shift (texture from source,
     *  tone from destination). `replace` stamps the source
     *  pixels directly with no tone correction — same effect as
     *  Clone Stamp but invoked from the Heal variant, useful
     *  when the user wants a Heal-style brush feel without the
     *  blending. Pattern source and the Photoshop blend modes
     *  (multiply/screen/darken/lighten) are intentionally not
     *  shipped — see the commit message for the rationale. */
    mode: 'normal' | 'replace';
  };
  smudge: {
    strength: number;
    /** When true, the stroke starts with the foreground color
     *  for the first dab (matches Photoshop's "Finger Painting"
     *  toggle). */
    fingerPainting: boolean;
  };
  blur: { strength: number };
  sharpen: { strength: number };
  dodge: {
    range: 'shadows' | 'midtones' | 'highlights';
    exposure: number;
  };
  burn: {
    range: 'shadows' | 'midtones' | 'highlights';
    exposure: number;
  };
  sponge: {
    mode: 'saturate' | 'desaturate';
    flow: number;
  };
}

/** Patch shape accepted by the Retouch reducer / UI callbacks.
 *  Top-level scalars (size, opacity, etc.) and the `variant`
 *  enum stay as their full type when included; per-variant
 *  nested blocks accept partials so a single field (e.g.
 *  `{ smudge: { strength: 50 } }`) can be updated without
 *  having to spread the rest of the variant's settings at the
 *  call site. The reducer's nested-merge in
 *  `useImageEditorState.ts` is what makes this safe. */
export type RetouchSettingsPatch = {
  [K in keyof RetouchSettings]?: RetouchSettings[K] extends object
    ? Partial<RetouchSettings[K]>
    : RetouchSettings[K];
};

export interface TextSettings {
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  underline: boolean;
  textAlign: 'left' | 'center' | 'right';
  fill: string;
  /** Arch curvature for the text path. -100 to 100; 0 = flat. */
  arch: number;
  /** Tier 2 Group B — letter spacing (tracking). Units are
   *  thousandths of an em, matching Fabric's `charSpacing`. 0 = no
   *  added space; positive values open the text up; negative values
   *  tighten it. */
  letterSpacing: number;
  /** Tier 2 Group B — line height (leading). Multiplier of
   *  `fontSize`, matching Fabric's `lineHeight`. 1 = packed, 1.16 =
   *  Fabric's default look, 2 = double-spaced. */
  lineHeight: number;
  /** Tier 2 Group B — text stroke (outline). Absent = no stroke;
   *  present = render with the given width/color and apply the
   *  position semantics described on `TextStrokePosition`. */
  stroke?: TextStrokeSettings;
  /** Tier 2 Group B — drop shadow. Absent = no shadow; present =
   *  render with the given offset/blur/color via `fabric.Shadow`. */
  dropShadow?: TextShadowSettings;
}

/** Tier 2 Group B. Photoshop convention:
 *  - `outside` — stroke renders fully outside the glyph silhouette
 *    (paintFirst='stroke' in Fabric — fill drawn after stroke covers
 *    the inner half).
 *  - `center` — stroke straddles the glyph edge (default canvas2d
 *    behavior; half inside, half outside).
 *  - `inside` — stroke renders fully inside the glyph silhouette.
 *    Approximated by `paintFirst:'fill'` + a self-shaped clipPath
 *    (the IText's own glyph runs as a mask) and a doubled effective
 *    strokeWidth so the visible width inside the glyph matches the
 *    user-set value. */
export type TextStrokePosition = 'outside' | 'center' | 'inside';

export interface TextStrokeSettings {
  width: number;
  color: string;
  position: TextStrokePosition;
}

export interface TextShadowSettings {
  color: string;
  offsetX: number;
  offsetY: number;
  blur: number;
}

export interface ShapeSettings {
  type: ShapeType;
  /** Solid hex (`#rrggbb`), `'transparent'`, or a gradient value
   *  serialized as a discriminated union. The legacy plain-string
   *  shape (used by older sessions and most of the codebase) maps
   *  to `{ type: 'solid', color: string }`. */
  fill: string | ShapeFillValue;
  stroke: string;
  strokeWidth: number;
  cornerRadius: number;
  /** Tier 2 Group A — primitive-specific knobs. Each field only
   *  applies to its corresponding `ShapeType`; the rest of the
   *  fields are inert when a non-matching type is active. */
  polygonSides?: number;
  starPoints?: number;
  /** 0..1 — ratio of inner radius to outer radius for the Star
   *  primitive. 0.5 reads as a "default" 5-point star; 0.95 makes
   *  it nearly a polygon, 0.1 makes it spiky. */
  starInnerRadiusRatio?: number;
  arrowHeadShape?: 'triangle' | 'open' | 'diamond';
  /** Arrow head size in scene-space pixels (NOT a ratio of length). */
  arrowHeadSize?: number;
}

/** Pen-tool settings — Tier 2 Group A. Drives the PenPanel +
 *  pen-creation handlers in `useFabricCanvas`. */
export interface PenSettings {
  variant: PenVariant;
  /** Auto-add / auto-delete anchors when clicking near a segment
   *  or an existing anchor while a path is being constructed.
   *  Photoshop default = true; some users disable to avoid
   *  destructive clicks during fine work. */
  autoAddDelete: boolean;
  /** Stroke style applied to every path the pen tool creates. */
  strokeColor: string;
  strokeWidth: number;
  /** Fill picker uses the existing `ShapeFillValue` union so the
   *  gradient picker UI is shared with shape layers. */
  fill: string | ShapeFillValue;
  /** Freeform-only: snap-to-edges magnetic tracking. */
  freeformMagnetic: boolean;
  /** Freeform-only: stroke-following width sensitivity (0–100). */
  freeformWidth: number;
}

/** Stop in a shape gradient — duplicated locally rather than
 *  importing from `ColorSwatchPopover` to avoid a UI → types
 *  cycle. Keep these shapes in sync with the popover types. */
export interface ShapeGradientStop {
  offset: number;
  color: string;
}

export type ShapeFillValue =
  | { type: 'solid'; color: string }
  | { type: 'linear'; angle: number; stops: ShapeGradientStop[] }
  | { type: 'radial'; stops: ShapeGradientStop[] };

export interface MarqueeSettings {
  shape: MarqueeShape;
  forceSquare: boolean;
  color: string;
  /** Magic-wand tolerance: max per-channel (R/G/B) difference allowed (0–255). */
  tolerance: number;
  /** Magic-wand contiguous mode: only adjacent pixels of similar color are selected. */
  contiguous: boolean;
}

export interface ExportSettings {
  // ExportSettings travels via `onExportFias` (raster-only path —
  // PSD takes the separate `onExportPsd` route). Narrow to the
  // canvas-native union so the receiver doesn't need a runtime
  // 'psd' guard.
  format: CanvasNativeExportFormat;
  quality: number;
  width: number;
  height: number;
  maintainAspectRatio: boolean;
}

export interface CropState {
  isActive: boolean;
  aspectRatio: number | null;
  rect: { left: number; top: number; width: number; height: number } | null;
}

/** A single guide line projected over the canvas at a fixed
 *  scene-space coordinate. Horizontal guides span the full width
 *  at `position` Y; vertical guides span the full height at
 *  `position` X. Tier 2 Group D workspace UI. */
export interface Guide {
  /** Stable id — survives reorder, used as React key. Generated
   *  by the reducer when the guide is added (caller passes only
   *  axis + position). */
  id: string;
  axis: 'horizontal' | 'vertical';
  /** Scene-space coordinate (Y for horizontal guides, X for
   *  vertical). Persists with the document so reloading restores
   *  guide positions. */
  position: number;
}

/** Configuration for the optional measurement grid overlay
 *  (`CanvasOverlays/Grid`). Distinct from the transparency
 *  checkerboard rendered by CanvasArea's CSS underlay. */
export interface GridConfig {
  /** Scene-space pixel spacing between major grid lines. Default
   *  100; user-configurable via the Grid Settings dialog. */
  size: number;
  /** Number of minor subdivisions inside each major cell. 4 = a
   *  light line every 25px when `size: 100`. Use 1 for major-only. */
  subdivisions: number;
  /** Stroke color for both major and minor lines. Minor lines
   *  render at 40% opacity for visual hierarchy. */
  color: string;
}

/** Workspace overlay state — toggleable chrome (rulers, grid,
 *  guides) and the snap behaviors that ride on top. Persisted in
 *  the IDB session alongside the document so reloading restores
 *  the user's working setup. */
export interface ViewSettings {
  /** Top + left ruler panels with tick marks + cursor tracking
   *  line. Default off — matches Photoshop's Ctrl+R toggle. */
  rulersVisible: boolean;
  /** Measurement grid overlay (NOT the transparency checkerboard
   *  in CanvasArea — that's a separate underlay). Default off. */
  gridVisible: boolean;
  /** Render all `guides` over the canvas. Independent of grid /
   *  rulers visibility — the user might want guides visible while
   *  rulers stay hidden. Default true so newly-dragged guides
   *  show up without needing a second toggle. */
  guidesVisible: boolean;
  gridConfig: GridConfig;
  guides: Guide[];
  /** When true, guides become non-interactive (no drag-to-move,
   *  no drag-off-canvas delete). Photoshop's View → Lock Guides. */
  lockGuides: boolean;
  /** When true, dragging a layer with the Move tool snaps to
   *  nearby guide lines within tolerance. Default true — guides
   *  exist to snap against. */
  snapToGuides: boolean;
  /** When true, dragging a layer snaps to grid intersections.
   *  Default off — opt-in for explicit pixel-aligned layout. */
  snapToGrid: boolean;
  /** Tier 2 Group D Smart Guides — when true, dragging a layer
   *  snaps its edges/centers to other visible layers' edges and
   *  centers. The match fires a magenta indicator line + distance
   *  label via `CanvasOverlays/SmartGuides`. Holding Ctrl mid-
   *  drag temporarily disables (per-drag escape hatch). Default
   *  true — the headline UX of the feature. */
  smartGuidesEnabled: boolean;
}

export interface ImageEditorState {
  activeTool: EditorTool;
  brushSettings: BrushSettings;
  textSettings: TextSettings;
  shapeSettings: ShapeSettings;
  marqueeSettings: MarqueeSettings;
  retouchSettings: RetouchSettings;
  /** Tier 1 source-of-truth for the layer tree. Reducer cases that
   *  modify layers update `document` and then derive `layers`
   *  (below) so consumers reading the flat ordered list keep
   *  working unchanged. */
  document: EditorDocument;
  /** Derived: depth-first ordered flat list of every layer in the
   *  document (front-most first). Kept in lockstep with `document`
   *  by the reducer; do not write to it directly. Tier 0 callers
   *  read this; Tier 1 group-aware UI reads `document` directly. */
  layers: Layer[];
  activeLayerId: string | null;
  /** Per-layer edit target: which surface receives brush /
   *  eraser strokes for that layer. Default 'layer' (paint into
   *  the layer's pixel surface — Tier 0 behavior). 'mask' routes
   *  strokes to the layer's mask alpha — brush paints white
   *  (reveals), eraser clears the mask (hides). Preserved per
   *  layer across selection changes — switching to a different
   *  layer and back keeps the layer's last edit target. */
  editTargets: Record<string, 'layer' | 'mask'>;
  layersPanelOpen: boolean;
  crop: CropState;
  zoom: number;
  canvasWidth: number;
  canvasHeight: number;
  /** Tier 3 Group D Slice 1d — document DPI for print pipelines and
   *  unit conversions. `null` means "no DPI declared" (legacy
   *  documents + most web-oriented imports); export paths that need
   *  a physical size fall back to the typical web default (72) but
   *  don't write the metadata. Templates that ship with a `dpi`
   *  pre-populate this when they spawn a new document.
   *
   *  Tier 4 candidate: dpi-aware resize (Image Size dialog) — out of
   *  scope here; the Document Settings dialog only edits metadata,
   *  not pixel dimensions. */
  dpi: number | null;
  selectedObjectIds: string[];
  fileName: string;
  fileId: string | null;
  isDirty: boolean;
  /** Move tool's Auto-Select toggle. `true` (default) keeps the
   *  current behavior: clicking a layer on canvas makes it the active
   *  object and lets you drag it. `false` (Photoshop's Move-tool
   *  default) drags whichever layer is currently active no matter
   *  where on canvas the click lands — to work on a different layer,
   *  switch via the Layers panel. Surfaced as a checkbox in the Move
   *  tool's properties panel. */
  autoSelectLayers: boolean;
  propertiesPanelOpen: boolean;
  /** Currently-displayed AI sub-panel (e.g. 'inpaint', 'upscale'),
   *  or `null` when the user has selected the AI tool but not yet
   *  picked a capability — in that state the AIToolPickerDialog is
   *  shown instead of a sub-panel. */
  selectedAITool: AICapability | null;
  /** Whether the AI tool picker dialog is open. */
  aiPickerOpen: boolean;
  /** Quick Mask mode active flag (Tier 1 Group D). When true the
   *  active tool is forced to `'quickmask'` and brush strokes paint
   *  on a global alpha mask rendered as a red overlay. On exit the
   *  mask is converted back into a wand-style marquee. The flag is
   *  kept in state so the StatusBar chip popover and any future
   *  command-palette entry stay in sync. The previous tool is
   *  preserved in `quickMaskPrevTool` so toggling off restores it. */
  quickMaskActive: boolean;
  /** Editor tool active just before Quick Mask was entered, so
   *  exiting QM restores the previous workflow rather than dropping
   *  the user into Move. Null when Quick Mask is inactive. */
  quickMaskPrevTool: EditorTool | null;
  /** Quick Mask brush mode. Default `'add'` (paint white into the
   *  alpha mask → grow the selection). `'subtract'` paints alpha=0
   *  → shrink. Holding Alt at mouse-down inverts whichever mode is
   *  active for that single stroke, matching Photoshop. Persisted
   *  across QM entries so a user who prefers Erase keeps it. */
  quickMaskMode: 'add' | 'subtract';
  /** Free Transform mode (Tier 1 Group E Phase 1). Modal — when
   *  active, the target layer's Fabric object becomes the active
   *  object with mode-specific controls; the TransformPanel
   *  replaces the active-tool's PropertiesPanel surface. The mode
   *  is not a tool-palette slot — entered via Ctrl/Cmd+T or the
   *  layer overflow menu. */
  transformActive: boolean;
  /** Free Transform sub-mode. Free/Skew use Fabric's native
   *  affine transform (scaleX/scaleY/angle/skewX/skewY). Distort,
   *  Perspective, Warp are projective/mesh deformations that
   *  Fabric can't render natively — they preview via overlay
   *  handles and bake on Apply through custom rasterization. */
  transformMode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp';
  /** Layer id under transform — captured at BEGIN so the apply /
   *  revert paths target the right object even if the user changes
   *  active layer mid-session (which shouldn't happen, but guard
   *  defensively). Null when transform isn't active. */
  transformLayerId: string | null;
  /** Pen-tool settings — Tier 2 Group A. */
  penSettings: PenSettings;
  /** Path Edit mode flag (Tier 2 Group A). Modal — when active,
   *  the target vector layer's anchors and handles render on
   *  canvas as draggable Fabric objects; the EditPathPanel
   *  replaces the active-tool's PropertiesPanel surface. Entered
   *  via double-tap on a vector layer's thumbnail, the layer
   *  overflow menu's "Edit Path…" entry, or ESC during a pen
   *  draw. Mirrors the Free Transform pattern. */
  pathEditActive: boolean;
  /** Layer id under Path Edit — captured at BEGIN. Null when
   *  Path Edit isn't active. */
  pathEditLayerId: string | null;
  /** Which path is being edited — the layer's own `path` (vector
   *  layer rows, default) or the layer's mask's `vectorPath`
   *  (vector mask rows; Tier 2 Group A PR-D). The Apply step
   *  dispatches to a different action based on this flag. */
  pathEditTarget: 'layer' | 'mask';
  /** Index of the currently-selected anchor within the layer's
   *  active sub-path (always sub-path 0 in v1; multi-sub-path
   *  selection is a future enhancement). `null` = no anchor
   *  selected, in which case clicking on the canvas creates a new
   *  anchor at a position computed by the renderer. */
  pathEditSelectedAnchorIndex: number | null;
  /** Tier 2 Group D workspace overlays + snap toggles. Persisted
   *  with the session so layouts survive reload. */
  viewSettings: ViewSettings;
  /** Tier 2 Group F sub-piece #5 — named rectangular regions
   *  defined by the Slice tool. Authoritative source for the
   *  ExportDialog's Region → Slices mode. Empty by default; new
   *  entries are appended on slice creation. Persisted with the
   *  session so a tab reload restores the user's slice layout. */
  slices: SliceRect[];
}

export type ImageEditorAction =
  | { type: 'SET_ACTIVE_TOOL'; tool: EditorTool }
  | { type: 'SET_SELECTED_AI_TOOL'; tool: AICapability | null }
  | { type: 'SET_AI_PICKER_OPEN'; open: boolean }
  /** Enter / leave Quick Mask mode (Tier 1 Group D). The reducer
   *  swaps `activeTool` to/from `'quickmask'` and snapshots /
   *  restores `quickMaskPrevTool` in one transaction so the toggle
   *  is atomic. Picture editing side-effects (seeding from the
   *  current selection, installing the visual overlay, converting
   *  the mask back into a marquee on exit) live in EditorPage —
   *  the reducer only owns the flag transitions. */
  | { type: 'SET_QUICK_MASK_ACTIVE'; active: boolean }
  | { type: 'SET_QUICK_MASK_MODE'; mode: 'add' | 'subtract' }
  /** Enter Free Transform on a specific layer. The reducer
   *  snapshots the target layer id + sets `transformMode` to
   *  `'free'` (the default mode any entry uses; user can switch
   *  mid-session via the panel's mode picker). The canvas-side
   *  capture-and-lock happens in EditorPage's effect. */
  | { type: 'BEGIN_TRANSFORM'; layerId: string }
  | {
      type: 'SET_TRANSFORM_MODE';
      mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp';
    }
  /** Leave Free Transform. `apply: true` keeps the user's edits
   *  (Fabric matrix stays for affine modes; baked pixels for
   *  non-affine). `apply: false` (Cancel) restores the captured
   *  initial state. */
  | { type: 'END_TRANSFORM'; apply: boolean }
  /** Pen-tool settings update (Tier 2 Group A). Partial patch —
   *  fields not in the patch keep their existing values. */
  | { type: 'UPDATE_PEN_SETTINGS'; settings: Partial<PenSettings> }
  /** Enter Path Edit mode on a specific vector layer or vector
   *  mask. The reducer flips `pathEditActive` + captures the
   *  layer id + the target (layer-path vs mask-path) + resets the
   *  selected anchor index. The canvas-side overlay setup happens
   *  in EditorPage's effect, mirroring the Free Transform pattern.
   *
   *  `target` defaults to `'layer'` when omitted — back-compat
   *  for existing callers that edit a vector layer's own path.
   *  `'mask'` is the Tier 2 Group A PR-D entry-point for editing
   *  a vector mask's path. */
  | { type: 'BEGIN_PATH_EDIT'; layerId: string; target?: 'layer' | 'mask' }
  | { type: 'SET_PATH_EDIT_ANCHOR'; index: number | null }
  /** Leave Path Edit. `apply: true` keeps the edits; `false`
   *  (Cancel) restores the snapshotted path (caller-side ref). */
  | { type: 'END_PATH_EDIT'; apply: boolean }
  /** Replace the path on a vector LayerNode wholesale. Used by
   *  the pen-tool finish path (initial creation) and the Path
   *  Edit Apply step. No-op when the layer isn't a vector node.
   *
   *  `clearVectorSource: true` ALSO drops the layer's
   *  `vectorSource` marker — used by Path Edit's Apply so the
   *  slider-driven regenerate flow stops treating a hand-edited
   *  polygon as a "regular polygon" and preserves the user's
   *  custom anchor edits when they later change color / sides /
   *  stroke. */
  | { type: 'SET_VECTOR_PATH'; id: string; path: VectorPath; clearVectorSource?: boolean }
  /** Convert a layer (vector / shape / text) to a `paint` raster
   *  layer. Used when the user accepts the rasterize-before-warp
   *  prompt — the layer's current rendering has been swapped on
   *  the Fabric side already; this just brings the document model
   *  in line by changing `type` and clearing the type-specific
   *  fields (`path`, `vectorSource`). The Fabric object identity
   *  (and therefore `fabricObjectId`) stays the same because the
   *  swap re-tags the new FabricImage with the original layerId. */
  | { type: 'CONVERT_LAYER_TO_PAINT'; id: string }
  /** Patch a single anchor on a vector layer (used during Path
   *  Edit's per-drag tick). `subPathIndex` defaults to 0 — v1
   *  edits sub-path 0 only. No-op when ids/indices are out of
   *  range. */
  | {
      type: 'UPDATE_VECTOR_ANCHOR';
      id: string;
      anchorIndex: number;
      subPathIndex?: number;
      anchor: Partial<VectorAnchor>;
    }
  | { type: 'UPDATE_BRUSH_SETTINGS'; settings: Partial<BrushSettings> }
  | { type: 'UPDATE_TEXT_SETTINGS'; settings: Partial<TextSettings> }
  | { type: 'UPDATE_SHAPE_SETTINGS'; settings: Partial<ShapeSettings> }
  | { type: 'UPDATE_MARQUEE_SETTINGS'; settings: Partial<MarqueeSettings> }
  /** Update RetouchSettings. Top-level fields (variant, size,
   *  hardness, opacity, etc.) merge directly; per-variant nested
   *  blocks merge via spread (so `{ clone: { aligned: false } }`
   *  patches just `aligned` while keeping `sample` intact). The
   *  reducer handles both kinds in one case. */
  | { type: 'UPDATE_RETOUCH_SETTINGS'; settings: RetouchSettingsPatch }
  | { type: 'SET_ZOOM'; zoom: number }
  | { type: 'SET_CANVAS_DIMENSIONS'; width: number; height: number }
  /** Tier 3 Group D Slice 1d — set the document's DPI metadata.
   *  Pass `null` to clear it (e.g. when a non-print template
   *  spawns a new document and the user wants to drop the
   *  inherited value). */
  | { type: 'SET_DPI'; dpi: number | null }
  | { type: 'SET_CROP'; crop: Partial<CropState> }
  | { type: 'SET_SELECTION'; objectIds: string[] }
  | { type: 'SET_FILE_INFO'; fileName: string; fileId: string | null }
  | { type: 'MARK_DIRTY' }
  | { type: 'MARK_CLEAN' }
  | { type: 'SET_AUTO_SELECT_LAYERS'; value: boolean }
  | { type: 'TOGGLE_PROPERTIES_PANEL' }
  | { type: 'TOGGLE_LAYERS_PANEL' }
  | { type: 'ADD_LAYER'; layer: Layer; setActive?: boolean }
  /** Insert a new adjustment layer with `layerId` as the new
   *  node's id and `name` as the user-facing display name. The
   *  reducer creates the `LayerNode` (`type: 'adjustment'`,
   *  `adjustment: config`) and routes through `addNode` using the
   *  caller's `parentId` / `index` (default: root, front-most).
   *  `clipToBelow` is stored verbatim — the slow-path compositor
   *  reads it to scope the adjustment to the immediately-preceding
   *  leaf's silhouette. `setActive` defaults to true. */
  | {
      type: 'ADD_ADJUSTMENT';
      layerId: string;
      name: string;
      config: AdjustmentConfig;
      parentId?: string | null;
      index?: number;
      clipToBelow?: boolean;
      setActive?: boolean;
    }
  /** Patch the config of an existing adjustment-layer node. The
   *  config replaces the layer's existing `adjustment` field
   *  wholesale — partial patches happen on the caller side. The
   *  reducer no-ops when the id isn't an `adjustment`-type
   *  node. */
  | { type: 'UPDATE_ADJUSTMENT'; id: string; config: AdjustmentConfig }
  | { type: 'REMOVE_LAYER'; id: string }
  | { type: 'RENAME_LAYER'; id: string; name: string }
  | { type: 'TOGGLE_LAYER_VISIBILITY'; id: string }
  | { type: 'SET_LAYER_OPACITY'; id: string; opacity: number }
  | { type: 'SET_LAYER_BLEND_MODE'; id: string; blendMode: BlendMode }
  | { type: 'SET_LAYER_EFFECTS'; id: string; effects: LayerEffects }
  | { type: 'SET_LAYER_RASTER_MODIFIED'; id: string; modified: boolean }
  /** Attach (or detach with `null`) a mask to a layer. The Fabric-
   *  side mask object — including its underlying pixel canvas — is
   *  managed by `useFabricCanvas.addLayerMask` /
   *  `removeLayerMask`; this action only updates the document
   *  config (LayerMask metadata + fabricObjectId reference). */
  | { type: 'SET_LAYER_MASK'; id: string; mask: LayerMask | null }
  /** Replace the vector path on a vector mask. Used by Path Edit
   *  Apply when the user was editing a mask's path, and by the
   *  raster→vector conversion path. No-op when the layer has no
   *  mask, or when the mask's kind isn't `'vector'`. Tier 2 Group
   *  A PR-D. */
  | { type: 'SET_MASK_VECTOR_PATH'; id: string; path: VectorPath }
  /** Switch a mask between raster and vector kinds. Vector → raster
   *  drops `vectorPath` and keeps RAW intact (the rasterized
   *  snapshot becomes the new source of truth — lossless). Raster
   *  → vector seeds `vectorPath` with the caller-supplied geometry
   *  (typically a traced contour from the existing RAW alpha or a
   *  canvas-sized rect when RAW is empty). Caller is responsible
   *  for re-rasterizing into RAW after the kind switch so RAW
   *  matches the new path. No-op when the layer has no mask, or
   *  when the requested kind already matches. Tier 2 Group A PR-D. */
  | { type: 'CONVERT_MASK_KIND'; id: string; kind: 'raster' | 'vector'; vectorPath?: VectorPath }
  /** Bind (or unbind with `null`) a text layer to a vector layer's
   *  path. Tier 2 Group A PR-E. The Fabric-side IText `path`
   *  property is updated by `useFabricCanvas.bindTextToPath` —
   *  this action just records the binding in the document so it
   *  survives save / undo and so the text-path-sync effect in
   *  EditorPage knows which vector to track. No-op when `id` isn't
   *  a text layer or the referenced vector doesn't exist (binding
   *  to a missing path would render nothing). */
  | { type: 'SET_TEXT_PATH'; id: string; binding: TextPathBinding | null }
  | { type: 'SET_EDIT_TARGET'; id: string; target: 'layer' | 'mask' }
  /** Wrap a contiguous list of sibling layers in a new group node.
   *  The reducer uses `lib/layer-tree.groupNodes` which validates
   *  the ids share a parent and exist; failures throw at dispatch
   *  time. `groupId` is supplied by the caller so the same id
   *  flows through to the active-layer setter. */
  | { type: 'GROUP_LAYERS'; ids: string[]; groupId: string; name?: string }
  /** Inverse of GROUP_LAYERS — flatten the named group's children
   *  back into its parent, then delete the group node. */
  | { type: 'UNGROUP'; id: string }
  /** Tree-aware move: relocate a layer to a new (parentId, index)
   *  in the document. Replaces flat-index reorder for drag-into-
   *  group / drag-out-of-group. The reducer routes through
   *  `lib/layer-tree.moveNode` which validates against cyclic
   *  parenting. */
  | { type: 'MOVE_NODE_IN_TREE'; id: string; parentId: string | null; index: number }
  | { type: 'SET_ACTIVE_LAYER'; id: string | null }
  | { type: 'REORDER_LAYER'; fromIndex: number; toIndex: number }
  /** Bulk-replace every layer. Accepts EITHER a flat list (Tier 0
   *  callers and the IndexedDB migration use this) or a fully-
   *  built document (group-aware callers). The reducer normalizes
   *  the flat-list form via `fromFlatLayers`. */
  | { type: 'RESET_LAYERS'; document: EditorDocument; activeLayerId: string | null }
  | { type: 'RESET_LAYERS_FLAT'; layers: Layer[]; activeLayerId: string | null }
  /** Tier 2 Group D workspace UI — patch any subset of view-
   *  settings fields. Partial-merge against the current state so
   *  callers only specify what changed (toggle a single flag,
   *  update one grid-config field, etc.). Guide-list mutations
   *  have their own dedicated actions below for array-aware
   *  reducer logic. */
  | { type: 'UPDATE_VIEW_SETTINGS'; patch: Partial<Omit<ViewSettings, 'guides'>> }
  /** Reducer assigns the guide id; caller only supplies axis +
   *  position. Idempotent against identical axis + position (no
   *  duplicate added at the same coordinate). */
  | { type: 'ADD_GUIDE'; axis: 'horizontal' | 'vertical'; position: number }
  | { type: 'MOVE_GUIDE'; id: string; position: number }
  | { type: 'REMOVE_GUIDE'; id: string }
  | { type: 'CLEAR_GUIDES' }
  /** Bulk-replace the view-settings (session restore path). Used
   *  by `RESET_LAYERS` / `RESET_LAYERS_FLAT` callers that loaded
   *  a persisted session — the reducer can't infer this from the
   *  patch action because optional fields would default. */
  | { type: 'RESET_VIEW_SETTINGS'; viewSettings: ViewSettings }
  /** Tier 2 Group F sub-piece #5 — Slice tool reducer actions.
   *  The reducer mints the slice id at creation time so callers
   *  (canvas overlay drag-finish, panel "+ Slice" button) only
   *  supply geometry + optional name. Names default to
   *  "Slice N" where N is the next unused 1-indexed counter
   *  scanning the existing slice array (so undo + re-add doesn't
   *  reuse a stale number). Zero-area or negative-area rects are
   *  rejected (no-op). */
  | {
      type: 'ADD_SLICE';
      x: number;
      y: number;
      width: number;
      height: number;
      name?: string;
    }
  /** Patch an existing slice's geometry and/or name. Partial — only
   *  supplied fields are updated; identity preserved when the patch
   *  is a no-op against the current value. Width / height clamped
   *  to ≥ 1 (the panel's numeric inputs and the Fabric resize
   *  controls both can momentarily produce 0; clamping rather than
   *  rejecting keeps the user from getting stuck with a slice that
   *  silently refuses to shrink). No-op when the id is unknown. */
  | {
      type: 'UPDATE_SLICE';
      id: string;
      patch: Partial<Omit<SliceRect, 'id'>>;
    }
  | { type: 'REMOVE_SLICE'; id: string }
  | { type: 'CLEAR_SLICES' }
  /** Bulk-replace the slice array (session restore path). Mirrors
   *  RESET_VIEW_SETTINGS — RESET_LAYERS callers that loaded a
   *  persisted session dispatch this to thread slices through. */
  | { type: 'RESET_SLICES'; slices: SliceRect[] }
  /** Translate every slice by (dx, dy). Used by the canvas-resize
   *  flow so slices stay anchored to the same image pixels after the
   *  canvas is shifted by an anchor-derived offset. */
  | { type: 'SHIFT_SLICES'; dx: number; dy: number };

export interface ProcessImageRequest {
  sourceFileId?: string;
  sourceDataUrl?: string;
  operations: ImageOperation[];
  output: {
    // Server-side Sharp processing — png/jpeg/webp/avif. Structured
    // formats (PSD / SVG / PDF) and TIFF bypass this path entirely
    // (no server-side support in v1; dialog forces those formats to
    // local-only via `isLocalOnlyExportFormat`).
    format: FiasUploadFormat;
    quality?: number;
    width?: number;
    height?: number;
    maintainAspectRatio?: boolean;
  };
  saveAs?: { fileName: string };
}

export type ImageOperation =
  | { type: 'resize'; width: number; height: number; fit?: 'cover' | 'contain' | 'fill' }
  | { type: 'rotate'; degrees: number }
  | { type: 'flip'; direction: 'horizontal' | 'vertical' }
  | { type: 'crop'; left: number; top: number; width: number; height: number }
  | { type: 'brightness'; value: number }
  | { type: 'contrast'; value: number }
  | { type: 'saturation'; value: number }
  | { type: 'grayscale' }
  | { type: 'blur'; sigma: number }
  | { type: 'sharpen'; sigma?: number };

export interface ProcessImageResponse {
  fileId?: string;
  downloadUrl?: string;
  width: number;
  height: number;
  sizeBytes: number;
  format: CanvasNativeExportFormat;
}

/** Status of the AI tool's in-flight pipeline. Renders as an inline
 *  banner in the AIPanel + as a full-screen overlay during model load
 *  / inference. */
export type AIStatus =
  | { kind: 'idle' }
  | { kind: 'loading-model'; progressPct?: number }
  | { kind: 'running'; label: string }
  | { kind: 'error'; message: string };

/** Smart Create's two output modes:
 *
 *   • `blend`  — mask-aware inpaint that bakes generated pixels into
 *                the active layer with feathered edges. Best for
 *                modifying existing scene content. Requires a marquee.
 *   • `insert` — text-to-image + automatic background removal, added
 *                as a new editable layer. Best for adding a fresh
 *                subject. Marquee determines size + position;
 *                without one, the layer lands at canvas center. */
export type SmartCreateMode = 'blend' | 'insert';

/** Picker keys for Smart Create's Insert mode — each maps server-side
 *  to a (provider, apiModelName) tuple. Each model has a particular
 *  strength surfaced in the UI:
 *
 *    - `flux-2-pro`   (default) — photorealistic, fast (BFL FLUX 2 Pro)
 *    - `flux-ultra`             — 4 MP / hi-res (BFL FLUX 1.1 Pro Ultra)
 *    - `gpt-image-1`            — best text rendering (OpenAI)
 *    - `nano-banana`            — fastest + cheapest, multi-image
 *                                  composition (Google Nano Banana)
 *    - `imagen-4`               — photorealistic alternative (Google)
 *    - `recraft-v3`             — illustration / icon styles (Recraft)
 *    - `stable-ultra`           — 18 named style presets (Stability)
 *
 *  Blend mode is forced to OpenAI gpt-image-1 since it's the only
 *  mask-aware model wired today. */
export type SmartCreateInsertModel =
  | 'flux-2-pro'
  | 'flux-ultra'
  | 'gpt-image-1'
  | 'nano-banana'
  | 'imagen-4'
  | 'recraft-v3'
  | 'stable-ultra';
